import { createAuthenticatedWebSocket } from "../socketService";
import { Notification } from "types/notification.interface";

type MessageHandler = (msg: Notification) => void;
type TokenRefreshHandler = () => Promise<string>;

const RETRY_DELAY = 2000; // 2 seconds

function connectNotifications(
  url: string,
  token: string,
  rights: number,
  onMessage: MessageHandler,
  onOpen?: () => void,
  onClose?: (e: CloseEvent) => void,
  onError?: (e: Event) => void,
  onTokenExpired?: TokenRefreshHandler,
  onAuthFailure?: (message: string) => void
) {
  let socket: WebSocket | null = null;
  let authenticated = false;
  let reconnectAttempt = 0;
  let retryTimeout: NodeJS.Timeout | null = null;
  let isDisconnected = false;
  let authFailure: "expired" | "rejected" | null = null;

  function createSocket() {
    if (isDisconnected) return;

    socket = createAuthenticatedWebSocket(url, token, rights);

    socket.onopen = () => {
      try {
        console.debug("notificationsSocketService: socket connected, sending auth...");
        // Send authentication message
        const authMessage = JSON.stringify({
          type: "auth",
          token,
          rights
        });
        socket!.send(authMessage);
        console.debug("notificationsSocketService: auth message sent");
      } catch (e) {
        console.error("notificationsSocketService: error sending auth message", e);
      }
    };

    socket.onmessage = (ev: MessageEvent) => {
      try {
        const parsed = JSON.parse(ev.data);
        
        // Handle authentication response
        if (parsed.type === "auth" && parsed.status === "ok") {
          authenticated = true;
          reconnectAttempt = 0;
          console.debug("notificationsSocketService: authenticated successfully");
          if (onOpen) onOpen();
          return;
        }
        
        // Handle error responses
        if (parsed.type === "error") {
          authFailure = parsed.code === "TOKEN_EXPIRED" ? "expired" : "rejected";
          if (authFailure === "rejected") {
            console.error("notificationsSocketService: authentication rejected");
          }
          socket?.close(1008, authFailure === "expired" ? "Token expired" : "Authentication failed");
          return;
        }
        
        // Handle notifications
        if (authenticated) {
          const n: Notification = parsed as Notification;
          onMessage(n);
        }
      } catch (err) {
        console.error("notificationsSocketService: error parsing message", err, ev.data);
      }
    };

    socket.onerror = (ev) => {
      console.error("notificationsSocketService: socket error", ev);
      if (onError) onError(ev);
    };

    socket.onclose = (ev) => {
      console.debug("notificationsSocketService: socket closed", ev.code, ev.reason);
      authenticated = false;
      if (!isDisconnected && authFailure === "expired") {
        authFailure = null;
        if (onTokenExpired) {
          void onTokenExpired()
            .then((newToken) => {
              if (!isDisconnected) {
                token = newToken;
                reconnectAttempt = 0;
                createSocket();
              }
            })
            .catch((error: unknown) => {
              const message = error instanceof Error ? error.message : "Unable to renew WebSocket authentication";
              console.error("notificationsSocketService: token renewal failed", message);
              onAuthFailure?.(message);
            });
        } else {
          onAuthFailure?.("WebSocket authentication expired");
        }
      } else if (!isDisconnected && authFailure === "rejected") {
        authFailure = null;
        onAuthFailure?.("WebSocket authentication was rejected");
      } else if (!isDisconnected && ev.code !== 1000) {
        reconnectAttempt++;
        console.warn(`notificationsSocketService: reconnecting (attempt ${reconnectAttempt})...`);
        retryTimeout = setTimeout(() => {
          retryTimeout = null;
          createSocket();
        }, RETRY_DELAY);
      }
      
      if (onClose) onClose(ev);
    };
  }

  // Create initial socket
  createSocket();

  return {
    disconnect: () => {
      try {
        isDisconnected = true;

        // Clear any pending retry
        if (retryTimeout) {
          clearTimeout(retryTimeout);
          retryTimeout = null;
        }
        
        if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) {
          socket.close(1000, "Client disconnect");
        }
      } catch (e) {
        console.warn("notificationsSocketService: error closing socket", e);
      }
    },
    rawSocket: () => socket,
  };
}

export default connectNotifications;
